import { API_BASE } from './apiBase.js';

export class PlaceAlertError extends Error {
	/** @param {string} code @param {number} [status] */
	constructor(code, status = 0) { super(code); this.code = code; this.status = status; }
}

/** @param {(force:boolean)=>Promise<string|null>} getToken @param {{signal?:AbortSignal, base?:string, fetcher?:typeof fetch}} [options] */
export function makePlaceAlertsClient(getToken, { signal, base = API_BASE, fetcher = fetch } = {}) {
	/** @param {string} path @param {string} [method] @param {any} [body] */
	async function request(path, method = 'GET', body) {
		const controller = new AbortController();
		const abort = () => controller.abort();
		if (signal?.aborted) abort();
		else signal?.addEventListener('abort', abort, { once: true });
		const timeout = setTimeout(abort, 15_000);
		try {
		for (const refresh of [false, true]) {
			const token = await getToken(refresh);
			if (!token) throw new PlaceAlertError('not_signed_in', 401);
			const response = await fetcher(`${base}/v1/place-alerts${path}`, {
				method, signal: controller.signal, credentials: 'omit', cache: 'no-store',
				headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
				...(body === undefined ? {} : { body: JSON.stringify(body) })
			});
			if (response.status === 401 && !refresh) continue;
			const data = await response.json().catch(() => null);
			if (!response.ok) throw new PlaceAlertError(typeof data?.error === 'string' ? data.error : 'request_failed', response.status);
			return data;
		}
		} finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
	}
	/** @param {{subjectUid:string,id:string}} rule */
	const path = (rule) => `/${encodeURIComponent(rule.subjectUid)}/${encodeURIComponent(rule.id)}`;
	return {
		list: () => request(''),
		/** @param {any} rule */ create: (rule) => request('', 'POST', rule),
		/** @param {{subjectUid:string,id:string}} rule */ get: (rule) => request(path(rule)),
		/** @param {{subjectUid:string,id:string}} rule @param {string} action */ action: (rule, action) => request(path(rule), 'PATCH', { action }),
		/** @param {{subjectUid:string,id:string}} rule */ remove: (rule) => request(path(rule), 'DELETE'),
		/** @param {string} id @param {any} subscription @param {string} locale */ register: (id, subscription, locale) => request(`/devices/${encodeURIComponent(id)}`, 'PUT', { platform: 'web', subscription, locale }),
		/** @param {string} id */ unregister: (id) => request(`/devices/${encodeURIComponent(id)}`, 'DELETE')
	};
}
